import { Box, ListItem, Button, BUTTON_VARIANTS, SpinnerIcon, Toast } from '@community_dev/pixels'
import { faCheckSquare, faComment, faLink, faInfoCircle, faUser } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import mixpanel from 'mixpanel-browser'
import type { JSX } from 'react'
import { useMap, useList } from 'react-use'
import styled, { useTheme } from 'styled-components'

import { getDownloadUrl } from 'api/download'
import { ClientDetail } from 'components/ClientDetail'
import { CONTEXT_TOKEN } from 'constants/web-storage'
import { useUserContext } from 'contexts/UserProvider'

type DownloadError = {
  text: string
  subtext?: string
}
const StyledListItem = styled(ListItem)`
  > div > div:last-child {
    min-height: 80px;
    min-width: 81px;
  }
`
const StyledButton = styled(Button)`
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION1};
  font-weight: 600;
  color: ${({ theme }) => theme?.COLORS?.TEXT};
  position: relative;
  top: 32px;
  border-color: ${({ theme }) => theme?.COLORS?.DIVIDERS};
  padding: 7px 12px;
  min-width: 0;
  line-height: 20px;
  > span {
    display: inline-block;
  }
`

export type FileDetails = {
  label: string
  subtext: string
  faIcon: any
  requiresFlagEnabled?: string[]
  requiresCapabilityEnabled?: string[]
}
export type FileDownloadMap = Record<string, FileDetails>

const fileNameMap: FileDownloadMap = {
  'campaigns.csv.gz': {
    label: 'Campaign Performance',
    subtext: 'Time sent, message text, recipient count, response count, stop count, etc.',
    faIcon: faComment,
  },
  'outbound_message_type_usage.csv.gz/segment-based-subscription': {
    label: 'Message Segment Usage',
    subtext: 'Detailed usage report with message type and daily breakouts',
    faIcon: faComment,
  },
  'campaign_links.csv.gz': {
    label: 'Campaign Link Performance',
    subtext: 'Link type, link url, recipient count, hit count, message ID, etc.',
    faIcon: faLink,
  },
  'members.csv.gz': {
    label: 'Member Details',
    subtext: 'First and last name, email, birthday, gender identity, city, state, etc.',
    faIcon: faInfoCircle,
  },
  'member_term_accepts.csv.gz': {
    label: 'Member Term Accepts',
    subtext: 'Member term accepts recorded over the last 14 days',
    faIcon: faCheckSquare,
    requiresCapabilityEnabled: ['feature:data_download:member_term_accepts'],
  },
  'member_state_changes.csv.gz': {
    label: 'Member Subscription Status',
    subtext: 'Changes in the subscription state of members (start, stop, etc)',
    faIcon: faUser,
  },
  'custom_member_data.csv.gz': {
    label: 'Custom Member Data',
    subtext: 'All custom Member data from Member signup field, integrations, self-uploaded CSVs',
    faIcon: faUser,
    requiresCapabilityEnabled: ['feature:custom_member_data:all'],
  },
  'communities.csv.gz': {
    label: 'Communities',
    subtext: 'Community Name, ID, and Member count',
    faIcon: faUser,
  },
  'member_communities.csv.gz': {
    label: 'Community Membership',
    subtext: 'Member ID, Community ID, and the date that the Member was added to the Community',
    faIcon: faUser,
  },
}

export const DownloadsList = (): JSX.Element => {
  const { COLORS } = useTheme() || {}
  const { context, logout, capabilities: userCapabilities } = useUserContext()
  const [loading, { set: setLoading }] = useMap<Record<string, boolean>>({})
  const [errors, { removeAt: removeErrorByIndex, push: addError }] = useList<DownloadError>([])

  const download = (fileName: string, fileDetails: FileDetails) => {
    setLoading(fileName, true)
    getDownloadUrl(fileName, context.clientId, sessionStorage.getItem(CONTEXT_TOKEN) || '')
      .then((url) => {
        const link = document.createElement('a')
        link.style.display = 'none'
        link.target = '_blank'
        link.href = url
        let event
        try {
          event = new MouseEvent('click')
        } catch (e) {
          event = document.createEvent('MouseEvent')
          event.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null)
        }

        link.dispatchEvent(event)

        mixpanel.track(`Data Export ${fileDetails.label} downloaded`)
      })
      .catch((err) => {
        const fileLabel = fileNameMap[fileName].label
        let errorText = `Could not download: ${fileLabel}`
        let errorSubtext = ''
        if (err.name === 'ApiError' && err.status === 404) {
          errorText = `No data found: ${fileLabel}`
          errorSubtext = 'Data will not be available until 24hrs after being enabled for this account.'
        }
        if (err.name === 'ApiError' && (err.status === 401 || err.status === 403)) {
          errorText = `Permission denied: ${fileLabel}`
        }
        addError({ text: errorText, subtext: errorSubtext })
      })
      .finally(() => {
        setLoading(fileName, false)
      })
  }
  return (
    <Box
      css={`
        margin-top: 0;
      `}
    >
      {errors.length > 0 && (
        <Box.ToastArea>
          {errors.map(
            (error: DownloadError, index: number): JSX.Element => (
              <Toast
                icon
                key={error.text + index}
                message={error.text}
                onDismiss={() => {
                  removeErrorByIndex(index)
                }}
                subtext={error.subtext}
                success={false}
              />
            ),
          )}
        </Box.ToastArea>
      )}
      <Box.Content>
        <h1>Download Account Data</h1>
        <ClientDetail clientImage={context.clientImage} name={context.name} phoneNumber={context.phoneNumber} />
        <div
          css={`
            > :last-child > div > div {
              border-bottom: none;
            }
          `}
        >
          {Object.entries(fileNameMap)
            .filter(([, details]) => {
              if (details.requiresCapabilityEnabled !== undefined && details.requiresCapabilityEnabled.length > 0) {
                return details.requiresCapabilityEnabled.every((cap) => userCapabilities.includes(cap))
              }

              return true
            })
            .map(([fileName, details]): JSX.Element => {
              return (
                <StyledListItem
                  key={fileName}
                  label={details.label}
                  prefix={<FontAwesomeIcon color={COLORS?.SUBTEXT} icon={details.faIcon} />}
                  subtext={details.subtext}
                  suffix={
                    <StyledButton
                      disabled={loading[fileName]}
                      onClick={(): void => download(fileName, details)}
                      variant={BUTTON_VARIANTS.PILL}
                    >
                      {loading[fileName] ? <SpinnerIcon /> : 'Download'}
                    </StyledButton>
                  }
                />
              )
            })}
        </div>
        <p
          css={`
            ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION1};
            font-weight: 500;
            text-align: center;
          `}
        >
          Data exports are refreshed every 24 hours.
        </p>
      </Box.Content>

      <Box.Footer>
        {window.opener ? (
          <Button onClick={() => window.close()} variant={BUTTON_VARIANTS.LINK}>
            Back to Dashboard
          </Button>
        ) : (
          <Button onClick={logout} variant={BUTTON_VARIANTS.LINK}>
            Logout
          </Button>
        )}
      </Box.Footer>
    </Box>
  )
}
